/**
 * Offboarding one customer: ask here, a different admin decides on the
 * Offboarding page. Soft deactivates and can be undone; hard erases, can't be
 * undone, and is refused while their ledger still points at what it would erase.
 * There is no undo button: reversing a soft offboard is an engineering task.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Badge, Button } from '@lipaflow/ui'
import { offboardError, offboarding, type OffboardMode } from '../../services/offboarding'
import { useSector } from '../../sector/sector'

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const labelCls = 'block text-caption text-ink-muted mb-1'

export function OffboardSection({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const queryClient = useQueryClient()
  const sector = useSector().code
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<OffboardMode>('soft')
  const [reason, setReason] = useState('')
  const [confirmName, setConfirmName] = useState('')

  const requests = useQuery({ queryKey: ['offboard-requests', sector], queryFn: () => offboarding.list(sector) })
  const reset = () => { setOpen(false); setMode('soft'); setReason(''); setConfirmName('') }
  const ask = useMutation({
    mutationFn: () => offboarding.request(tenantId, { mode, reason: reason.trim(), confirm_tenant_name: confirmName.trim() }),
    onSuccess: () => {
      toast.success('Asked: a different admin decides it on the Offboarding page')
      queryClient.invalidateQueries({ queryKey: ['offboard-requests'] })
      reset()
    },
    onError: (e) => toast.error(offboardError(e, "Couldn't ask to offboard")),
  })

  const pending = (requests.data ?? []).find((r) => r.tenant_id === tenantId && (r.status === 'pending' || r.status === 'approved'))
  const confirmed = confirmName.trim() === tenantName.trim()
  const ready = reason.trim().length >= 10 && confirmed

  return (
    <section aria-labelledby="customer-offboard" className="rounded-card border border-hairline p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 id="customer-offboard" className="font-inter text-caption text-ink-muted">Offboarding</h4>
        {pending && <Badge variant="warning" size="sm">{pending.mode === 'hard' ? 'Erase asked' : 'Offboard asked'}: waiting for a second admin</Badge>}
      </div>

      {pending && (
        <p className="text-body-sm text-ink">
          “{pending.reason}”. A different admin approves or rejects it on the Offboarding page.
        </p>
      )}

      {!pending && !open && (
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>Offboard this customer…</Button>
      )}

      {!pending && open && (
        <div className="space-y-3">
          <fieldset className="space-y-2">
            <legend className={labelCls}>How</legend>
            <label className="flex items-start gap-2 text-body-sm text-ink">
              <input type="radio" name="offboard-mode" checked={mode === 'soft'} onChange={() => setMode('soft')} className="mt-1" />
              <span><span className="font-medium">Deactivate.</span> No one can sign in, no messages go out and renewals stop. Nothing is deleted, so an engineer can reverse it.</span>
            </label>
            <label className="flex items-start gap-2 text-body-sm text-ink">
              <input type="radio" name="offboard-mode" checked={mode === 'hard'} onChange={() => setMode('hard')} className="mt-1" />
              <span><span className="font-medium text-warning">Erase their data.</span> Irreversible. Refused while their payment records still point at it.</span>
            </label>
          </fieldset>
          <label className="block"><span className={labelCls}>Why (at least 10 characters; it goes on the activity log as written)</span>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={field}
              placeholder="e.g. Contract ended on 31 Oct; they asked us to close the account" />
          </label>
          <label className="block">
            <span className={labelCls}>Type <span className="text-ink font-medium">{tenantName}</span> exactly to confirm</span>
            <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className={field} autoComplete="off" aria-label="Type the customer's name exactly to confirm" />
          </label>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={reset} disabled={ask.isPending}>Cancel</Button>
            <Button onClick={() => ask.mutate()} isLoading={ask.isPending} disabled={!ready}>
              {mode === 'hard' ? 'Ask to erase' : 'Ask to deactivate'}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
